import { bundlePostageDiscountLabel } from "./checkoutPricing";

/**
 * Normalized financial breakdown for saved orders.
 * Backend stores coupon/code discount on `order.discountPrice` (despite the name).
 * Bundle-rule discounts are subtracted in checkout but not stored as a field;
 * we derive them from totals when possible.
 */

const round2 = (n) => Number((Number(n) || 0).toFixed(2));

/**
 * Sum of qty * unit price from cart lines (pre order-level coupon / bundle rule).
 */
export function cartItemsSubtotal(cart) {
  if (!Array.isArray(cart)) return 0;
  return round2(
    cart.reduce(
      (acc, item) =>
        acc + Number(item?.qty || 0) * Number(item?.discountPrice ?? item?.price ?? 0),
      0
    )
  );
}

/**
 * Postage total from cart lines (may not match charged shipping if free-shipping code applied).
 */
export function cartPostageTotal(cart) {
  if (!Array.isArray(cart)) return 0;
  return round2(
    cart.reduce(
      (acc, item) =>
        acc +
        Number(item?.qty || 0) *
          Number(item?.postageFees ?? item?.postageFee ?? item?.shippingFee ?? 0),
      0
    )
  );
}

/**
 * Charged shipping on the order (preferred when present).
 */
export function orderShippingCharged(order) {
  if (!order) return 0;
  const s = order.shipping;
  if (s !== undefined && s !== null && String(s) !== "") {
    return round2(s);
  }
  return cartPostageTotal(order?.cart || []);
}

/**
 * Items subtotal as stored at checkout (matches backend `subTotalPrice` when set).
 */
export function orderItemsSubtotal(order) {
  if (!order) return 0;
  const stored = order.subTotalPrice;
  if (stored !== undefined && stored !== null && Number(stored) >= 0) {
    return round2(stored);
  }
  return cartItemsSubtotal(order?.cart || []);
}

/**
 * Coupon / code discount for this seller order (stored as `order.discountPrice`).
 */
export function orderCouponDiscount(order) {
  return round2(order?.discountPrice ?? 0);
}

/**
 * Seller bundle-rule discount. Prefer value stored on the order; otherwise derive from totals.
 */
export function deriveBundleRuleDiscount(order) {
  if (!order) return 0;
  const stored = order.bundleDiscount;
  if (stored !== undefined && stored !== null && Number(stored) > 0) {
    return round2(stored);
  }
  const sub = orderItemsSubtotal(order);
  const ship = orderShippingCharged(order);
  const bpf = round2(order?.buyerProtectionFee ?? 0);
  const coupon = orderCouponDiscount(order);
  const bundlePostage = deriveBundlePostageDiscount(order);
  const total = round2(order?.totalPrice ?? 0);
  const raw = round2(sub + ship + bpf - coupon - bundlePostage - total);
  return raw > 0 ? raw : 0;
}

export function deriveBundlePostageDiscount(order) {
  if (!order) return 0;
  const stored = order.bundlePostageDiscount;
  if (stored !== undefined && stored !== null && Number(stored) >= 0) {
    return round2(stored);
  }
  const cartPostage = cartPostageTotal(order?.cart || []);
  const charged = orderShippingCharged(order);
  const raw = round2(cartPostage - charged);
  return raw > 0 ? raw : 0;
}

export function orderPostageBeforeBundleDiscount(order) {
  const fromCart = cartPostageTotal(order?.cart || []);
  const charged = orderShippingCharged(order);
  const bundlePostage = deriveBundlePostageDiscount(order);
  if (fromCart > 0) return fromCart;
  return round2(charged + bundlePostage);
}

export function bundlePostageLabelForBreakdown(breakdown, fallbackPct = 30) {
  const before = Number(breakdown?.postageBeforeBundle ?? 0);
  const amt = Number(breakdown?.bundlePostageDiscount ?? 0);
  if (before > 0 && amt > 0) {
    return bundlePostageDiscountLabel(Math.round((amt / before) * 100));
  }
  return bundlePostageDiscountLabel(fallbackPct);
}

export function getLineOriginalUnitPrice(item) {
  if (!item) return 0;
  const v = item.originalPrice ?? item.bundleOriginalPrice ?? item.original_price;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function getLinePaidUnitPrice(item) {
  if (!item) return 0;
  const n = Number(item.discountPrice ?? item.price ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Full buyer-facing breakdown. Prefers line-item math when it disagrees materially with stored `totalPrice`.
 */
export function getBuyerOrderBreakdown(order) {
  const itemsSubtotal = orderItemsSubtotal(order);
  const shipping = orderShippingCharged(order);
  const buyerProtectionFee = round2(order?.buyerProtectionFee ?? 0);
  const couponDiscount = orderCouponDiscount(order);
  const bundleDiscount = deriveBundleRuleDiscount(order);
  const bundlePostageDiscount = deriveBundlePostageDiscount(order);
  const postageBeforeBundle = orderPostageBeforeBundleDiscount(order);
  const discountCode = (order?.discountCode && String(order.discountCode).trim()) || "";

  const storedTotal = round2(order?.totalPrice ?? 0);
  const recomputed = round2(
    itemsSubtotal + shipping + buyerProtectionFee - couponDiscount - bundleDiscount
  );
  const roundingDiff = round2(Math.abs(recomputed - storedTotal));

  const totalCharged = roundingDiff > 0.02 ? recomputed : storedTotal;

  return {
    itemsSubtotal,
    shipping,
    buyerProtectionFee,
    couponDiscount,
    bundleDiscount,
    bundlePostageDiscount,
    postageBeforeBundle,
    totalCharged,
    discountCode,
    /** Difference between recomputed breakdown and stored `order.totalPrice`. */
    roundingDiff,
  };
}
